import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, ActivatedRoute, RouterModule } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { PageBreadcrumbComponent } from '../../../shared/components/common/page-breadcrumb/page-breadcrumb.component';
import { ComponentCardComponent } from '../../../shared/components/common/component-card/component-card.component';
import { ButtonComponent } from '../../../shared/components/ui/button/button.component';
import { DealContactRoleApiService } from '../services/deal-contact-role-api.service';
import {
  DealContactRoleRequest,
  DealContactRoleResponse,
} from '../models/deal-contact-role.model';
import { AuthService } from '../../auth-pages/services/auth.service';
import { PreventSpecialCharactersDirective } from '../../../shared/directives/prevent-special-characters.directive';
import { PreventLongNamesDirective } from '../../../shared/directives/prevent-long-names.directive';
import { PreventLongDescriptionsDirective } from '../../../shared/directives/prevent-long-descriptions.directive';
import { ValidatorFormsInformation } from '../../../shared/utils/validator-forms-information.utils';
import { ToastService } from '../../../shared/services/toast.service';

@Component({
  selector: 'app-deal-contact-role-form',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    FormsModule,
    PageBreadcrumbComponent,
    ComponentCardComponent,
    ButtonComponent,
    PreventSpecialCharactersDirective,
    PreventLongNamesDirective,
    PreventLongDescriptionsDirective,
  ],
  templateUrl: './deal-contact-role-form.component.html',
})
export class DealContactRoleFormComponent implements OnInit {
  loading = false;
  saving = false;
  error = '';
  isEditing = false;
  itemId: number | null = null;

  // Form fields. displayOrder no se edita aquí: el orden se define arrastrando en la lista.
  name = '';
  private originalName = '';
  description = '';
  color = '';
  isActive = true;

  dealContactRolesExisting: DealContactRoleResponse[] = [];

  constructor(
    private api: DealContactRoleApiService,
    private authService: AuthService,
    private router: Router,
    private route: ActivatedRoute,
    private toast: ToastService,
  ) {}

  ngOnInit(): void {
    const id = this.route.snapshot.paramMap.get('id');
    if (id) {
      this.isEditing = true;
      this.itemId = +id;
      this.cargar(this.itemId);
    }
    this.getDealContactRolesExisting();
  }

  getDealContactRolesExisting() {
    const businessId = this.authService.getBusinessId();
    if (!businessId) {
      this.error = 'No se identificó la empresa';
      return;
    }
    this.api.getAll(businessId).subscribe({
      next: (dealContactRoles) => {
        this.dealContactRolesExisting = dealContactRoles ?? [];
      },
      error: (err) => {
        this.toast.error({
          title: 'Prioridades',
          message:
            err.error?.message || 'No se pudo obtener prioridades existentes',
        });
      },
    });
  }

  setError(message: string): void {
    this.error = message;
  }

  private cargar(id: number): void {
    this.loading = true;
    this.error = '';

    const businessId = this.authService.getCurrentUser()?.businessId;
    if (!businessId) {
      this.error = 'No se pudo obtener el negocio del usuario';
      this.loading = false;
      return;
    }

    this.api.getById(id, businessId).subscribe({
      next: (item) => {
        this.name = item.name;
        this.originalName = item.name;
        this.description = item.description || '';
        this.color = item.color || '';
        this.isActive = item.isActive;
        this.loading = false;
      },
      error: (err) => {
        this.error = err.error?.message || 'Error al cargar el rol de contacto';
        this.loading = false;
      },
    });
  }

  get pageTitle(): string {
    return this.isEditing ? 'Editar Rol de Contacto' : 'Nuevo Rol de Contacto';
  }

  guardar(): void {
    const nameValidation = ValidatorFormsInformation.validateText(
      this.name,
      'nombre',
      30,
    );
    if (nameValidation) {
      this.error = nameValidation;
      this.toast.warning({ title: 'Datos no válidos', message: this.error });
      return;
    }

    const descriptionValidation = ValidatorFormsInformation.validateText(
      this.description,
      'descripción',
      500,
    );
    if (descriptionValidation) {
      this.error = descriptionValidation;
      this.toast.warning({ title: 'Datos no válidos', message: this.error });
      return;
    }

    const businessId = this.authService.getCurrentUser()?.businessId;
    if (!businessId) {
      this.error = 'No se pudo obtener el negocio del usuario';
      this.toast.error({ title: 'Error', message: this.error });
      return;
    }

    if (
      this.dealContactRolesExisting.some(
        (dealContactRole) =>
          dealContactRole.name.toLowerCase() === this.name.toLowerCase(),
      )
    ) {
      this.error = 'El nombre ya se encuentra en uso';
      this.toast.warning({ title: 'Nombre duplicado', message: this.error });
      return;
    }

    this.saving = true;
    this.error = '';

    // Sin displayOrder: al crear el backend lo pone al final; al editar conserva el actual.
    const request: DealContactRoleRequest = {
      name: this.name.trim(),
      description: this.description.trim() || undefined,
      color: this.color || undefined,
      isActive: this.isActive,
      businessId,
    };

    if (this.isEditing && this.itemId) {
      this.api.update(this.itemId, businessId, request).subscribe({
        next: () => {
          this.saving = false;
          this.router.navigate(['/config-deals/roles-contacto']);
          this.toast.updated('Rol de contacto',
            this.originalName !== this.name.trim()
              ? `Se actualizó "${this.originalName}" → "${this.name.trim()}".`
              : `Se actualizó "${this.name.trim()}" correctamente.`);
        },
        error: (err) => {
          this.saving = false;
          this.error =
            err.error?.message || 'Error al actualizar el rol de contacto';
          this.toast.error({ title: 'Error', message: this.error });
        },
      });
    } else {
      this.api.create(request).subscribe({
        next: () => {
          this.saving = false;
          this.router.navigate(['/config-deals/roles-contacto']);
          this.toast.created('Rol de contacto', `Se creó "${this.name.trim()}" correctamente.`);
        },
        error: (err) => {
          this.saving = false;
          this.error =
            err.error?.message || 'Error al crear el rol de contacto';
          this.toast.error({ title: 'Error', message: this.error });
        },
      });
    }
  }

  cancelar(): void {
    this.router.navigate(['/config-deals/roles-contacto']);
  }
}
